import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import Toast from '../components/Toast';

const reasonOf = (error) => error?.message || 'the upload did not complete';

const toFailedUpload = (file, error) => ({
  id: `failed-${uuidv4()}`,
  name: file.name,
  // The File itself is kept, not just its name: a retry that asks the teacher
  // to find the file again is not a retry.
  file,
  error: reasonOf(error),
});

const failuresOf = (files, results) =>
  results.flatMap((result, index) =>
    result.status === 'rejected' ? [toFailedUpload(files[index], result.reason)] : [],
  );

const successesOf = (results) =>
  results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));

const warnAboutFailures = (failures) => {
  if (!failures.length) return;
  toast(
    <Toast
      message={`${failures.length} file${failures.length > 1 ? 's' : ''} failed to upload. Retry ${failures.length > 1 ? 'them' : 'it'} from the list below.`}
    />,
  );
};

/**
 * Uploads a batch of files and keeps the ones that worked.
 *
 * Deliberately not Promise.all: one rejection there discards every sibling that
 * already reached storage, so a single corrupt file empties a 30-file drop and
 * leaves the teacher nothing to retry. Here each upload settles on its own, the
 * successes are returned, and the failures become rows that can be retried.
 *
 * @param {Function} uploadFile - async (file) => uploaded item
 */
export const useBulkFileUpload = ({ uploadFile }) => {
  const [failedUploads, setFailedUploads] = useState([]);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const dismissUpload = useCallback(
    (id) => setFailedUploads((prev) => prev.filter((upload) => upload.id !== id)),
    [],
  );

  const markFailed = useCallback(
    (id, error) =>
      setFailedUploads((prev) =>
        prev.map((upload) =>
          upload.id === id ? { ...upload, error: reasonOf(error) } : upload,
        ),
      ),
    [],
  );

  const uploadAll = useCallback(
    async (files) => {
      const results = await Promise.allSettled(files.map(uploadFile));
      if (!mounted.current) return [];
      const failures = failuresOf(files, results);
      setFailedUploads((prev) => [...prev, ...failures]);
      warnAboutFailures(failures);
      return successesOf(results);
    },
    [uploadFile],
  );

  // Resolves to the uploaded item, or to undefined if it failed again — the row
  // stays put in that case, carrying the newer reason.
  const retryUpload = useCallback(
    async (upload) => {
      const [result] = await Promise.allSettled([uploadFile(upload.file)]);
      if (!mounted.current) return undefined;
      if (result.status === 'rejected') return markFailed(upload.id, result.reason);
      dismissUpload(upload.id);
      return result.value;
    },
    [uploadFile, markFailed, dismissUpload],
  );

  return { uploadAll, retryUpload, dismissUpload, failedUploads };
};
